/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";
import { Icon_Add_Evidence } from "@assets/images";
import { IEvidence, ProgramData, ProgramSkill } from "@interfaces/Interfaces";
import ProgramInfoCard from "@components/home/ProgramInfoCard";
import ProgramCardButton from "./ProgramCardButton";
import * as EvidenceUtils from "@utils/evidenceUtils";
import { useMemo } from "react";
import { isSameDay, isSameMonth, subMonths, isAfter, isSameYear, format } from "date-fns";
import classNames from "classnames";
import Collapsible from "react-collapsible";
import { IonIcon } from "@ionic/react";
import { chevronDown, chevronUp } from "ionicons/icons";
import { userRolesSelector } from "@state/UserState";
import { useRecoilValue } from "recoil";

type Props = {
  buttonPressed: (...args: any) => any;
  buttonPressedNonCompliant: (...args: any) => any;
  skill: ProgramSkill;
  programEvidence: IEvidence[];
  program: ProgramData;
  programStartDate: Date | undefined;
};

const getDatesToShow = (startDate: Date | undefined): Date[] => {
  if (!startDate) {
    return [];
  }

  let dates: Date[] = [];

  const currentDate = new Date();

  if (isSameYear(startDate, currentDate)) {
    if (isSameMonth(startDate, currentDate)) {
      dates = [currentDate];
    } else {
      let date = new Date();

      while (isSameYear(date, currentDate) && (isAfter(date, startDate) || isSameMonth(date, startDate))) {
        dates.push(date);

        date = subMonths(date, 1);
      }
    }
  } else {
    let date = new Date();
    const twelveMonthsAgo = subMonths(currentDate, 12);
    const earliestDate = isAfter(startDate, twelveMonthsAgo) ? startDate : twelveMonthsAgo;
    let monthCount = 0;
    const maxMonths = 12;

    while (monthCount < maxMonths && (isAfter(date, earliestDate) || isSameMonth(date, earliestDate))) {
      dates.push(date);

      date = subMonths(date, 1);
      monthCount++;
    }
  }

  return dates;
};

const ProgramRoleRequiredSkillCard: React.FC<Props> = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const datesToShow = useMemo(() => getDatesToShow(props.programStartDate), [props.programStartDate]);

  const userRoles = useRecoilValue(userRolesSelector);

  const userRole = userRoles.find((role) => role.programID === props.program.ID);

  const complianceData = useMemo(() => userRole?.complianceData, [userRole]);

  const duration = useMemo(() => parseInt(props.skill.duration ?? 0, 10), [props.skill]);

  const evidenceWithSkills = useMemo(
    () =>
      props.programEvidence.filter(
        (evidence) => evidence.draft !== true && EvidenceUtils.evidenceHasSkills(evidence, props.program.ID)
      ),
    [props.programEvidence]
  );

  const evidenceWithRequiredSkill = useMemo(
    () => evidenceWithSkills.filter((evidence) => evidence.evidenceJSON["Skills Used"] === props.skill.Name),
    [evidenceWithSkills, props.skill]
  );

  const count = useMemo(() => {
    if (duration > 1) {
      const dateThreshold = new Date().setDate(1);

      return evidenceWithRequiredSkill.filter((evidence) => {
        const isAfterDateThreshold =
          isSameDay(new Date(evidence.date), dateThreshold) || isAfter(new Date(evidence.date), dateThreshold);
        const isSameAsCurrentMonth = isSameMonth(new Date(), new Date(evidence.date));

        return isAfterDateThreshold && isSameAsCurrentMonth;
      }).length;
    } else {
      return evidenceWithRequiredSkill.filter((evidence) => isSameMonth(new Date(), new Date(evidence.date))).length;
    }
  }, [props.programEvidence]);

  const numberRequiredInDuration = useMemo(
    () => parseInt(props.skill.numberRequiredInDuration ?? 0, 10),
    [props.skill]
  );

  const getCompliantStatus = (): "COMPLIANT" | "WARNING" | "ATTENTION" => {
    if (complianceData) {
      const complianceDataForSkill = complianceData.find((data) => data.skillID === props.skill.ID);

      if (complianceDataForSkill) {
        return complianceDataForSkill.status;
      }
    }

    return "COMPLIANT";
  };

  const getCompliantStatusText = (status: "COMPLIANT" | "WARNING" | "ATTENTION" | undefined): string | undefined => {
    if (status === "COMPLIANT" || status === undefined) {
      return props.skill.greenNoticeText ?? "";
    } else if (status === "ATTENTION") {
      return props.skill.amberNoticeText ?? "";
    } else if (status === "WARNING") {
      return props.skill.redNoticeText ?? "";
    }
  };

  const getSidePillText = (status: "COMPLIANT" | "WARNING" | "ATTENTION"): string => {
    if (status === "COMPLIANT") {
      return "Compliant";
    } else if (status === "ATTENTION") {
      return "Warning";
    } else if (status === "WARNING") {
      return "Stop";
    }

    return "Compliant";
  };

  const getSidePillColor = (status: "COMPLIANT" | "WARNING" | "ATTENTION"): "red" | "amber" | "green" => {
    if (status === "COMPLIANT") {
      return "green";
    } else if (status === "ATTENTION") {
      return "amber";
    } else if (status === "WARNING") {
      return "red";
    }

    return "green";
  };

  const getAddEvidenceButtonText = (status: "COMPLIANT" | "WARNING" | "ATTENTION"): string => {
    if (status === "WARNING") {
      return "Add Witnessed Intubation evidence";
    }

    return "Add new evidence";
  };

  const compliantStatus = useMemo(() => getCompliantStatus(), [complianceData]);

  const addEvidencePressed = () => {
    if (compliantStatus === "WARNING") {
      props.buttonPressedNonCompliant();
    } else {
      props.buttonPressed();
    }
  };

  return (
    <ProgramInfoCard
      title={props.skill.Name}
      sidePill={compliantStatus !== undefined}
      sidePillText={getSidePillText(compliantStatus)}
      sidePillColor={getSidePillColor(compliantStatus)}
    >
      <>
        <div className="flex-col">
          <div className="text-acc-13px leading-[1.54] tracking-[-0.05px] text-grey-70">
            {getCompliantStatusText(compliantStatus)}
          </div>
          <div className="flex !flex-row shrink-[-1] h-[44px] items-end">
            <div
              className={classNames(`!text-acc-34px font-bold leading-[1.29] text-grey-60`, {
                "!text-[#ED242B]": compliantStatus === "WARNING" && count === 0,
                "!text-orange-50": count > 0 && count < numberRequiredInDuration,
                "!text-[#06A14F]": count >= numberRequiredInDuration,
              })}
            >
              {`${count}`}
            </div>
            <div className="!text-acc-15px font-bold leading-[1.33] tracking-default text-grey-60 mb-[5px]">
              &nbsp;{`/ ${numberRequiredInDuration}`}
            </div>
          </div>
          <div className="flex h-[4px] flex-1 self-end rounder-[2px] bg-white mb-[7px] overflow-hidden">
            {Array.from({ length: numberRequiredInDuration }).map((_, index) => (
              <div
                className="w-full flex flex-row"
                key={index}
              >
                <div className={`h-[4px] flex grow rounded-[2px] bg-grey-30 ${index < count ? "!bg-[#09AB55]" : ""}`} />
                {index !== numberRequiredInDuration - 1 && <div className="w-[4px] h-[4px] bg-white" />}
              </div>
            ))}
          </div>
        </div>
        <ProgramCardButton
          buttonPressed={addEvidencePressed}
          buttonText={getAddEvidenceButtonText(compliantStatus)}
          icon={Icon_Add_Evidence}
          collapseIcon={compliantStatus === "WARNING"}
        />
        {datesToShow.length > 0 && (
          <Collapsible
            open={false}
            onOpening={() => setIsOpen(true)}
            onClosing={() => setIsOpen(false)}
            trigger={
              <div className="flex flex-row items-center justify-between cursor-pointer py-2 mt-2">
                <div className="text-acc-15px font-bold leading-none tracking-[-0.5] text-grey-90 acc-underline">
                  {"Monthly Breakdown"}
                </div>
                <IonIcon
                  icon={isOpen ? chevronUp : chevronDown}
                  className="text-grey-50 text-base"
                />
              </div>
            }
            transitionTime={250}
            transitionCloseTime={250}
          >
            <div className="mt-2">
              {datesToShow.map((date) => {
                const count = evidenceWithRequiredSkill.filter((evidence) =>
                  isSameMonth(date, new Date(evidence.date))
                ).length;

                const isActiveMonth = isSameMonth(date, new Date());

                return (
                  <div
                    key={date.toISOString()}
                    className="flex flex-col justify-center min-h-11 px-0 py-3 intubation-dates-border"
                  >
                    <div className="flex flex-row justify-between">
                      <div className="text-acc-15px font-normal leading-[1.33] tracking-default text-grey-90">
                        {format(date, "MMMM")}
                      </div>
                      <div className="flex flex-row">
                        <div
                          className={classNames(
                            "text-acc-15px font-bold leading-[1.33] tracking-default text-grey-60",
                            {
                              "!text-[#ED242B]": !isActiveMonth && count === 0,
                              "!text-orange-50": !isActiveMonth && count > 0 && count < numberRequiredInDuration,
                              "!text-[#06A14F]": !isActiveMonth && count >= numberRequiredInDuration,
                            }
                          )}
                        >{`${count}`}</div>
                        <div className="text-acc-15px font-bold leading-[1.33] tracking-default text-grey-60">
                          &nbsp;{`/ ${numberRequiredInDuration}`}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Collapsible>
        )}
      </>
    </ProgramInfoCard>
  );
};

export default ProgramRoleRequiredSkillCard;
